import { useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { cn } from '@/lib/utils';

import { AgentInboxDialogHeader } from './agent-inbox-dialog-header';
import {
  useAgentInboxAvailabilityQuery,
  useRenameAgentInboxAddressMutation,
} from './agent-inbox.query';

type AgentInboxAddressFormProps = {
  currentAddress: string;
  onDone: () => void;
};

type Availability = 'idle' | 'checking' | 'available' | 'unavailable';

export const AgentInboxAddressForm = ({
  currentAddress,
  onDone,
}: AgentInboxAddressFormProps) => {
  const [localPart, domain] = currentAddress.split('@');
  const [draft, setDraft] = useState(localPart);
  const [checked, setChecked] = useState('');
  const checkLater = useDebouncedCallback(setChecked, 300);
  const availability = useAgentInboxAvailabilityQuery(checked);
  const rename = useRenameAgentInboxAddressMutation();
  const candidate = draft.trim().toLowerCase();

  const status: Availability =
    candidate === localPart || !candidate
      ? 'idle'
      : candidate !== checked || availability.isFetching
        ? 'checking'
        : availability.data
          ? 'available'
          : 'unavailable';

  return (
    <form
      className="flex flex-col"
      onSubmit={(event) => {
        event.preventDefault();
        if (status !== 'available') return;
        rename.mutate(candidate, {
          onSuccess: (address) => {
            toast.success(`Your assistant is now ${address}`);
            onDone();
          },
        });
      }}
    >
      <AgentInboxDialogHeader
        title="Change address"
        description="Your old address keeps working, so nothing sent to it is lost."
        onBack={onDone}
      />
      <YStack className="gap-2 px-6 pt-2">
        <label
          htmlFor="agent-inbox-address"
          className="text-sm font-medium text-foreground"
        >
          New address
        </label>
        <XStack
          className={cn(
            'h-10 items-center rounded-lg border bg-surface px-3 transition-colors focus-within:border-accent',
            status === 'unavailable'
              ? 'border-danger-8 focus-within:border-danger-8'
              : 'border-border-strong'
          )}
        >
          <input
            id="agent-inbox-address"
            autoFocus
            maxLength={40}
            spellCheck={false}
            autoComplete="off"
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
              checkLater(event.target.value.trim().toLowerCase());
            }}
            className="min-w-0 flex-1 bg-transparent font-mono text-sm text-foreground outline-none"
          />
          <span className="shrink-0 font-mono text-sm text-foreground-muted">
            @{domain}
          </span>
        </XStack>
        <AvailabilityHint status={status} isError={rename.isError} />
      </YStack>
      <XStack className="gap-2 px-6 pb-6 pt-6">
        <Button
          type="button"
          variant="secondary"
          className="flex-1"
          onClick={onDone}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          className="flex-1"
          disabled={status !== 'available'}
          isLoading={rename.isPending}
          loadingText="Saving"
        >
          Save address
        </Button>
      </XStack>
    </form>
  );
};

const AvailabilityHint = ({
  status,
  isError,
}: {
  status: Availability;
  isError: boolean;
}) => {
  if (isError) {
    return (
      <p role="alert" className="text-xs text-danger-11">
        Couldn&apos;t change the address. Try again.
      </p>
    );
  }
  switch (status) {
    case 'checking':
      return (
        <XStack className="items-center gap-1.5 text-xs text-foreground-muted">
          <Icons.loader className="size-3.5 animate-spin" />
          Checking availability
        </XStack>
      );
    case 'available':
      return (
        <XStack className="items-center gap-1.5 text-xs text-success-11">
          <Icons.check className="size-3.5 shrink-0" />
          Available
        </XStack>
      );
    case 'unavailable':
      return (
        <p aria-live="polite" className="text-xs text-danger-11">
          That address is taken or reserved. Try another one.
        </p>
      );
    case 'idle':
      return (
        <p className="text-xs text-foreground-muted">
          Letters, numbers, dots and dashes. 3 to 40 characters.
        </p>
      );
  }
};
